/**
 * Realtime Voice Tutor — frontend constants (P0-4 Phase 2)
 *
 * Source of truth contracts mirrored from backend P0-4 scaffold:
 *   - REALTIME_ALLOWED_ACTION_TYPES_FRONTEND ↔ backend frozenset
 *     `REALTIME_ALLOWED_ACTION_TYPES` in
 *     `backend/app/services/realtime_session_service.py:135-149`
 *   - REALTIME_ERROR_CODE_MAP ↔ backend 4-code typed errors namespace
 *     (P0-4: `realtime_input_validation_failed` / `realtime_session_unavailable`
 *     / `realtime_tool_rejected` / `realtime_token_expired` — last one
 *     reserved-not-emitted)
 *   - REALTIME_VOICE_OPTIONS ↔ backend `_ALLOWED_VOICES` frozenset
 *     (post-merge hotfix `8e8d718`: 8-voice validation)
 *
 * IMPORTANT: any change to the backend frozenset MUST sync here, or Layer 0
 * filter in `realtimeUiActionExecutor.js` will drift from backend Layer 1 /
 * Layer 2 re-check, opening privilege escalation surface.
 */

/**
 * Layer 0 frontend filter — MUST mirror backend
 * `REALTIME_ALLOWED_ACTION_TYPES` (8 items) EXACTLY.
 * NOTE: `attention_guide` EXCLUDED per 太子判定 #2 (P0-4 cycle 1).
 */
export const REALTIME_ALLOWED_ACTION_TYPES_FRONTEND = Object.freeze(new Set([
  'highlight_elements',
  'dim_irrelevant',
  'focus_single_element',
  'show_value_tags',
  'pin_reference_value',
  'show_micro_step',
  'set_stage',
  'restore_stage',
  // D5 (Part 2) student-requested chart actions — mirrors the backend list.
  'set_element_visibility',
  'set_chart_view',
  'focus_view_on_element',
]));

/**
 * 8 voice options — UI select dropdown for `requested_voice` Pydantic field
 * on `POST /api/realtime/session`. Backend hotfix `8e8d718` adds frozenset
 * validation for these exact 8 values.
 */
export const REALTIME_VOICE_OPTIONS = Object.freeze([
  { value: 'alloy', label: 'アロイ' },
  { value: 'ash', label: 'アッシュ' },
  { value: 'ballad', label: 'バラード' },
  { value: 'coral', label: 'コーラル' },
  { value: 'echo', label: 'エコー' },
  { value: 'sage', label: 'セージ' },
  { value: 'shimmer', label: 'シマー' },
  { value: 'verse', label: 'ヴァース' },
]);

/**
 * 4-code typed errors → localized JP UX message map.
 * `realtime_token_expired` reserved-not-emitted, NOT displayed (silent reconnect).
 * Adding a 5th code is FORBIDDEN per P0-4 contract (namespace stability).
 */
export const REALTIME_ERROR_CODE_MAP = Object.freeze({
  realtime_input_validation_failed: 'グラフを先にアップロードしてください。',
  realtime_session_unavailable: '音声チューターは一時的に利用できません。少し待ってからお試しください。',
  realtime_tool_rejected: 'この操作は音声チューターでは実行できません。',
});

/**
 * VERIFIED via openai-realtime-console (C1 source quote):
 *   POST https://api.openai.com/v1/realtime/calls?model=<model_used>
 *   Headers: Authorization: Bearer <ephemeral_token>
 *            Content-Type: application/sdp
 *   Body: SDP offer (raw text)
 *   Response: SDP answer (raw text)
 *
 * `model_used` comes from `/api/realtime/session` response (server-resolved
 * from `OPENAI_REALTIME_MODEL` env, default `gpt-realtime-2`).
 */
export const buildOpenAIRealtimeSdpUrl = (modelUsed) =>
  `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(modelUsed)}`;

/**
 * C10: prompt user 30 seconds BEFORE `expires_at` for manual session renewal.
 * `setTimeout(promptRenew, (expires_at * 1000) - Date.now() - this_lead_ms)`.
 */
export const TOKEN_RENEWAL_PROMPT_LEAD_MS = 30_000;

/**
 * C4 sub-decision (4b): POST-then-apply timeout (5s).
 * On timeout the overlay is NOT applied (defensive — no fire-and-forget UI).
 */
export const TOOL_DISPATCH_TIMEOUT_MS = 5_000;

/**
 * C4 sub-decision (4d): `call_id` dedup window + cap.
 * Repeated `call_id` within `_WINDOW_MS` is skipped. Map size capped at
 * `_CAP` (memory leak guard for long sessions).
 */
export const TOOL_DISPATCH_DEDUP_WINDOW_MS = 1_000;
export const TOOL_DISPATCH_DEDUP_CAP = 100;

/**
 * D4: hard caps on the Realtime voice connection. The connection is a
 * client-held WebRTC peer link to OpenAI, so these are enforced client-side
 * (the backend cannot force-close a P2P call). This is distinct from
 * TOKEN_RENEWAL_PROMPT_LEAD_MS above, which is about the ephemeral TOKEN expiry.
 *
 * - MAX_SESSION_DURATION_MS: total voice-tutor time, measured from the initial
 *   connect() and NOT reset by token renewal (renewSession) — a budget/experience
 *   guard, since a left-open realtime stream bills per audio token, invisible to
 *   request-count/spike alerts.
 * - IDLE_DISCONNECT_MS: end the session after a stretch with no conversation
 *   activity (reset whenever the student or AI speaks).
 * - SESSION_LIMIT_WARNING_LEAD_MS: how long before either limit to warn the student.
 */
export const MAX_SESSION_DURATION_MS = 20 * 60 * 1000;   // 20 min hard cap
export const IDLE_DISCONNECT_MS = 5 * 60 * 1000;         // 5 min idle
export const SESSION_LIMIT_WARNING_LEAD_MS = 60_000;     // 60 s heads-up

/**
 * Data-channel event types that count as conversation activity for the idle
 * timer: user speech (VAD start), AI transcript, student transcript, and
 * finalized tool calls. Background/session bookkeeping events do NOT reset idle.
 */
export const IDLE_ACTIVITY_EVENT_TYPES = Object.freeze(new Set([
  'input_audio_buffer.speech_started',
  'response.output_audio_transcript.delta',
  'response.output_audio_transcript.done',
  'response.audio_transcript.done',
  'conversation.item.input_audio_transcription.completed',
  'conversation.item.input_audio_transcription.delta',
  'response.function_call_arguments.done',
]));
